import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import { formatRelativeTime } from "@/lib/format";
import { FAQ_CATEGORIES, FAQ_CATEGORY_COUNTS, FAQ_ITEMS, faqAnchor } from "@/pages/help/faqData";
import { resolveFaqView, useFaqOverrides } from "@/pages/help/faqContent";
import {
  RECENT_FAQ_LIMIT,
  readFaqFeedback,
  readRecentFaqKeys,
  writeFaqFeedback,
  writeRecentFaqKeys,
  type FaqFeedback,
} from "@/pages/help/helpStorage";
import { recordFaqFeedback } from "@/pages/help/faqFeedback";

interface FaqSectionProps {
  onNotHelpful?: (categoryId: string) => void;
}

export default function FaqSection({ onNotHelpful }: FaqSectionProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [activeCat, setActiveCat] = useState("all");
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [recent, setRecent] = useState<string[]>(readRecentFaqKeys);
  const [feedback, setFeedback] = useState<Record<string, FaqFeedback>>(readFaqFeedback);
  const [linkCopied, setLinkCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [notHelpfulKey, setNotHelpfulKey] = useState<string | null>(null);
  const { overrides } = useFaqOverrides();

  const views = useMemo(
    () => FAQ_ITEMS.map((item) => resolveFaqView(item, overrides[item.qKey], t)),
    [overrides, t],
  );

  const viewsByKey = useMemo(() => {
    const map = new Map<string, (typeof views)[number]>();
    views.forEach((view) => map.set(view.qKey, view));
    return map;
  }, [views]);

  useEffect(() => {
    writeRecentFaqKeys(recent);
  }, [recent]);

  useEffect(() => {
    writeFaqFeedback(feedback);
  }, [feedback]);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const match = FAQ_ITEMS.find((item) => faqAnchor(item.qKey) === hash);
    if (!match) return;
    setOpenKeys((prev) => (prev.includes(match.qKey) ? prev : [...prev, match.qKey]));
    setRecent((prev) => [match.qKey, ...prev.filter((k) => k !== match.qKey)].slice(0, RECENT_FAQ_LIMIT));
    window.setTimeout(() => {
      document.getElementById(faqAnchor(match.qKey))?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  }, []);

  const setKeyword = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true },
    );
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return views.filter((view) => {
      if (activeCat !== "all" && view.cat !== activeCat) return false;
      if (!kw) return true;
      const haystack = `${view.question} ${view.answer}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [views, activeCat, keyword]);

  const toggle = (qKey: string) => {
    const isOpen = openKeys.includes(qKey);
    if (isOpen) {
      setOpenKeys((prev) => prev.filter((k) => k !== qKey));
    } else {
      setOpenKeys((prev) => [...prev, qKey]);
      setRecent((prev) => [qKey, ...prev.filter((k) => k !== qKey)].slice(0, RECENT_FAQ_LIMIT));
    }
  };

  const revealQuestion = (qKey: string) => {
    setActiveCat("all");
    setKeyword("");
    setOpenKeys((prev) => (prev.includes(qKey) ? prev : [...prev, qKey]));
    window.setTimeout(() => {
      document.getElementById(faqAnchor(qKey))?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const copySearchLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard unavailable, ignore */
    }
  };

  const copyQuestionLink = async (item: { qKey: string; question: string }) => {
    try {
      const url = new URL(`${window.location.origin}${window.location.pathname}`);
      url.searchParams.set("q", item.question);
      url.hash = faqAnchor(item.qKey);
      await navigator.clipboard.writeText(url.toString());
      setCopiedKey(item.qKey);
      window.setTimeout(() => setCopiedKey((k) => (k === item.qKey ? null : k)), 2000);
    } catch {
      /* clipboard unavailable, ignore */
    }
  };

  const giveFeedback = (item: { qKey: string; cat: string }, value: FaqFeedback) => {
    setFeedback((prev) => ({ ...prev, [item.qKey]: value }));
    void recordFaqFeedback(item.qKey, item.cat, value);
    if (value === "notHelpful") {
      setNotHelpfulKey(item.qKey);
      onNotHelpful?.(item.cat);
    } else {
      setNotHelpfulKey(null);
    }
  };

  const resetFilters = () => {
    setActiveCat("all");
    setKeyword("");
  };

  const tabs = [{ id: "all", labelKey: "helpCenter.faqAll" }, ...FAQ_CATEGORIES];
  const recentItems = recent
    .map((qKey) => viewsByKey.get(qKey))
    .filter((view): view is (typeof views)[number] => Boolean(view));

  return (
    <section id="faq" className="scroll-mt-20 border-y border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-5xl px-4 py-14 md:px-6 md:py-16">
        <SectionHeading
          eyebrow={t("helpCenter.faqEyebrow")}
          title={t("helpCenter.faqTitle")}
          description={t("helpCenter.faqDesc")}
        />

        {recentItems.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-700">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-700">
                <i className="ri-history-line" />
              </span>
              {t("helpCenter.faqRecentTitle")}
            </span>
            {recentItems.map((view) => (
              <button
                key={view.qKey}
                type="button"
                onClick={() => revealQuestion(view.qKey)}
                className="max-w-full cursor-pointer truncate rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-foreground-950"
                title={view.question}
              >
                {view.question}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRecent([])}
              className="ml-auto cursor-pointer whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-800"
            >
              {t("helpCenter.faqRecentClear")}
            </button>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label={t("helpCenter.faqTitle")}>
            {tabs.map((tab) => {
              const count = tab.id === "all" ? FAQ_ITEMS.length : FAQ_CATEGORY_COUNTS[tab.id] ?? 0;
              const active = activeCat === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveCat(tab.id)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    active
                      ? "bg-secondary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-foreground-950"
                  }`}
                >
                  {t(tab.labelKey)}
                  <span
                    className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium ${
                      active ? "bg-background-50/25 text-background-50" : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-80">
            <span className="flex h-4 w-4 items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder={t("helpCenter.searchPlaceholder")}
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-9 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword("")}
                aria-label={t("helpCenter.searchClear")}
                className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <p className="text-xs text-foreground-500">
            {t("helpCenter.faqResultCount", { count: filtered.length })}
          </p>
          {keyword && (
            <button
              type="button"
              onClick={copySearchLink}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-primary-700 transition-colors hover:text-primary-800"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className={linkCopied ? "ri-check-line" : "ri-link"} />
              </span>
              {linkCopied ? t("helpCenter.faqLinkCopied") : t("helpCenter.faqCopyLink")}
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {filtered.map((item) => {
              const open = openKeys.includes(item.qKey);
              const fb = feedback[item.qKey];
              const isCopied = copiedKey === item.qKey;
              return (
                <div
                  key={item.qKey}
                  id={faqAnchor(item.qKey)}
                  className="border-b border-background-200 scroll-mt-24 last:border-b-0"
                >
                  <div className="flex items-center gap-1 px-3 py-2.5 sm:px-5 transition-colors hover:bg-background-100">
                    <button
                      type="button"
                      onClick={() => toggle(item.qKey)}
                      aria-expanded={open}
                      className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-4 py-1.5 text-left"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                          <i className="ri-question-line text-xs" />
                        </span>
                        <span className="text-sm font-medium text-foreground-950">{item.question}</span>
                        {item.recent && (
                          <span
                            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700"
                            title={
                              item.recentAt
                                ? t("helpCenter.faqRecentlyUpdatedTitle", { time: formatRelativeTime(item.recentAt) })
                                : t("helpCenter.faqRecentlyUpdated")
                            }
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-edit-2-line" />
                            </span>
                            {t("helpCenter.faqRecentlyUpdated")}
                          </span>
                        )}
                      </span>
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-500">
                        <i className={open ? "ri-subtract-line" : "ri-add-line"} />
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => copyQuestionLink(item)}
                      title={t("helpCenter.faqCopyItem")}
                      aria-label={t("helpCenter.faqCopyItem")}
                      className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent text-foreground-400 transition-colors hover:border-background-300 hover:bg-background-50 hover:text-foreground-700"
                    >
                      <i className={isCopied ? "ri-check-line text-primary-600" : "ri-link"} />
                    </button>
                  </div>
                  {open && (
                    <div className="px-3 pb-5 sm:px-5">
                      <p className="ml-8 border-l-2 border-secondary-200 pl-3 text-sm leading-relaxed text-foreground-600">
                        {item.answer}
                      </p>
                      <div className="ml-8 mt-4 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-foreground-500">{t("helpCenter.faqHelpfulQuestion")}</span>
                        <button
                          type="button"
                          onClick={() => giveFeedback(item, "helpful")}
                          className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-xs transition-colors ${
                            fb === "helpful"
                              ? "border-primary-300 bg-primary-100 text-primary-800"
                              : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                          }`}
                        >
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-thumb-up-line" />
                          </span>
                          {t("helpCenter.faqHelpful")}
                        </button>
                        <button
                          type="button"
                          onClick={() => giveFeedback(item, "notHelpful")}
                          className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-xs transition-colors ${
                            fb === "notHelpful"
                              ? "border-secondary-300 bg-secondary-100 text-secondary-900"
                              : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300"
                          }`}
                        >
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-thumb-down-line" />
                          </span>
                          {t("helpCenter.faqNotHelpful")}
                        </button>
                        {fb && (
                          <span className="text-xs text-foreground-500">{t("helpCenter.faqFeedbackThanks")}</span>
                        )}
                      </div>
                      {notHelpfulKey === item.qKey && (
                        <p className="ml-8 mt-2 flex items-center gap-1.5 text-xs text-primary-700">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-arrow-down-line" />
                          </span>
                          {t("helpCenter.faqNotHelpfulHint")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
              {t("helpCenter.faqEmpty")}
            </p>
            <p className="mt-2 max-w-md text-sm text-foreground-600">{t("helpCenter.faqEmptyDesc")}</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line" />
              </span>
              {t("helpCenter.faqReset")}
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-lg border border-background-200 bg-accent-50 px-6 py-5 text-center sm:flex-row sm:text-left">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-accent-950">
              <i className="ri-customer-service-2-line text-lg" />
            </span>
            <p className="text-sm font-medium text-foreground-950">{t("helpCenter.faqContactCta")}</p>
          </div>
          <a
            href="#contact"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("helpCenter.navContact")}
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-down-line" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}